import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { COUNTRIES, COUNTRY_BY_CODE, searchCountries } from '../../data/countries';

/**
 * CountrySelect — single-country dropdown with search + flag emoji.
 *
 * Props:
 *   value         — ISO-2 code (e.g. "SA"); "" for unset
 *   onChange(iso) — callback returning the selected ISO-2 code
 *   isRTL         — Arabic labels & layout
 *   placeholder, testId
 */
export const CountrySelect = ({
  value = '',
  onChange,
  isRTL = false,
  placeholder = '',
  testId = 'country-select',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  const selected = value ? COUNTRY_BY_CODE[value] : null;

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useMemo(() => searchCountries(q), [q]);
  const defaultPlaceholder = isRTL ? 'اختر دولة' : 'Select country';

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={`${testId}-wrap`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full h-12 px-4 inline-flex items-center gap-2.5 bg-white border-2 border-[rgba(20,15,10,0.10)] rounded-xl hover:border-flame-400 transition-colors text-[14px] ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
        data-testid={`${testId}-button`}
      >
        {selected ? (
          <>
            <span className="text-[18px] leading-none">{selected.flag}</span>
            <span className="flex-1 truncate text-ink-900">{isRTL ? selected.ar : selected.name}</span>
          </>
        ) : (
          <span className="flex-1 text-ink-400">{placeholder || defaultPlaceholder}</span>
        )}
        <ChevronDown className="w-4 h-4 text-ink-500" />
      </button>

      {open && (
        <div className="absolute top-full mt-2 z-50 w-full min-w-[280px] max-h-[360px] bg-white border-2 border-[rgba(20,15,10,0.10)] rounded-xl shadow-2xl overflow-hidden flex flex-col" data-testid={`${testId}-popover`}>
          <div className="relative p-2 border-b border-[rgba(20,15,10,0.06)]">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 ${isRTL ? 'right-4' : 'left-4'}`} />
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={isRTL ? 'ابحث...' : 'Search...'}
              className={`w-full h-10 ${isRTL ? 'pr-9 pl-3 text-right' : 'pl-9 pr-3'} text-[13px] bg-[#FAF7F2] rounded-lg focus:outline-none focus:ring-2 focus:ring-flame-500/30`}
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {results.length === 0 && (
              <div className="p-6 text-center text-[13px] text-ink-500">{isRTL ? 'لا توجد نتائج' : 'No results'}</div>
            )}
            {results.map(c => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange?.(c.code); setOpen(false); setQ(''); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-[13px] hover:bg-flame-50 transition-colors ${isRTL ? 'flex-row-reverse text-right' : ''} ${value === c.code ? 'bg-flame-50' : ''}`}
                data-testid={`${testId}-option-${c.code}`}
              >
                <span className="text-[18px] leading-none">{c.flag}</span>
                <span className="flex-1 truncate text-ink-900">{isRTL ? c.ar : c.name}</span>
                {value === c.code && <Check className="w-4 h-4 text-flame-600" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelect;
